import React, { useEffect, useState } from "react";
import { Star, Scissors, Heart, ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { whatsappUrl, reservationMessage } from "../../lib/whatsapp";

// Slideshow imagery: facade, dining room, owners, signature dishes
const SLIDES = [
  {
    src: "https://customer-assets.emergentagent.com/job_corte-tradicional/artifacts/nimrabcf_Gemini_Generated_Image_j7em8sj7em8sj7em.png",
    alt: "Fachada del restaurante GR a Cuchillo",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_corte-tradicional/artifacts/to7zr35o_comerdor.jpg",
    alt: "Sala del restaurante GR a Cuchillo",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_corte-tradicional/artifacts/qhzgpvw1_Raul-y-Isabel.jpg",
    alt: "Raúl e Isabel con jamón cortado a cuchillo",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_corte-tradicional/artifacts/vl4gyivz_3191ffe1-5a09-4649-a148-acec9456ae35.jpg",
    alt: "Gamba roja a la plancha - especialidad de la casa",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_corte-tradicional/artifacts/fjst21h6_6595ca50-dc6b-4421-9ea5-d233ed244dfe.jpg",
    alt: "Pulpo a la brasa",
  },
];

const SLIDE_INTERVAL_MS = 5000;

export default function Hero() {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const waReservation = whatsappUrl(reservationMessage(t));

  useEffect(() => {
    const id = setInterval(
      () => setActive((i) => (i + 1) % SLIDES.length),
      SLIDE_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#1A1A1A]"
    >
      {/* Slideshow */}
      <div className="absolute inset-0" data-testid="hero-slideshow">
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            data-testid={`hero-slide-${i}`}
            data-active={i === active ? "true" : "false"}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: i === active ? 1 : 0 }}
          />
        ))}
      </div>

      <div aria-hidden="true" className="absolute inset-0 bg-black/25" />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/30 to-transparent"
      />

      <div
        className="absolute top-28 md:top-32 right-6 md:right-12 z-10 reveal"
        style={{ animationDelay: "0.2s" }}
      >
        <div className="backdrop-blur-md bg-[#F9F6F0]/90 border border-white/20 py-3 px-5 flex items-center gap-3 shadow-2xl">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-[#8B1E0F] text-[#8B1E0F]" />
            ))}
          </div>
          <div className="text-xs">
            <div className="font-semibold tracking-wide text-[#1A1A1A]">4.7 / 5</div>
            <div className="text-[#5C5C5C]">Google · +120</div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 min-h-[100vh] flex flex-col justify-end pb-20 md:pb-28 pt-40 md:pt-48">
        <div className="max-w-4xl reveal">
          <div
            className="eyebrow !text-[#E8A87C] mb-6 drop-shadow-md"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </div>
          <h1
            data-testid="hero-h1"
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-white max-w-3xl"
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.6)" }}
          >
            {t.hero.h1Pre}{" "}
            <span className="italic text-[#E8A87C]">{t.hero.h1Highlight}</span>
            {t.hero.h1Post}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 max-w-2xl text-base md:text-lg text-white/95 leading-relaxed"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={waReservation}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-9 py-5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors group shadow-2xl"
            >
              <MessageCircle className="h-4 w-4" />
              {t.whatsapp.buttonLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#tienda"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white text-white hover:text-[#1A1A1A] border border-white px-9 py-5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors backdrop-blur-sm"
            >
              {t.tienda.navLabel}
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-tertiary"
              className="inline-flex items-center justify-center gap-2 text-white px-3 py-5 text-xs tracking-[0.2em] uppercase font-semibold hover:opacity-80 transition-opacity"
            >
              <MapPin className="h-4 w-4" />
              {t.hero.ctaSecondary}
            </a>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-x-10 max-w-3xl pt-10 border-t border-white/20">
            <TrustItem
              icon={<Star className="h-5 w-5 fill-[#E8A87C] text-[#E8A87C]" />}
              title={t.hero.badgeRating}
              sub={t.hero.badgeRatingSub}
              testid="trust-rating"
            />
            <TrustItem
              icon={<Scissors className="h-5 w-5 text-[#E8A87C]" />}
              title={t.hero.badgeCraft}
              sub={t.hero.badgeCraftSub}
              testid="trust-craft"
            />
            <TrustItem
              icon={<Heart className="h-5 w-5 text-[#E8A87C]" />}
              title={t.hero.badgeFamily}
              sub={t.hero.badgeFamilySub}
              testid="trust-family"
            />
          </div>

          <div className="mt-10 flex items-center gap-2.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-slide-dot-${i}`}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-[3px] transition-all duration-500 ${
                  i === active
                    ? "w-14 bg-[#E8A87C]"
                    : "w-7 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustItem({ icon, title, sub, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div className="mt-1">{icon}</div>
      <div>
        <div
          className="text-sm font-semibold text-white"
          style={{ textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}
        >
          {title}
        </div>
        <div
          className="text-xs text-white/80 mt-0.5"
          style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}
        >
          {sub}
        </div>
      </div>
    </div>
  );
}
